import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircle, Check, Copy, Link2, Loader2, RefreshCw, ShieldAlert } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { authHeaders } from '@/features/ai-chat/core/utils/authHeaders'
import { formatDateTime } from '@/lib/dateUtils'

interface Status { objects: number; ready: number; pending: number; files: number }
interface Built {
    manifestId: number; url: string; expiresAt: string; objectCount: number; fileCount: number
    /** Present when the link came from a previous build rather than this one. */
    created?: string; createdBy?: string | null; expired?: boolean
}

/**
 * One link for the whole recovery set, to paste into the PXE recovery server console.
 *
 * Three steps rather than one button, because the middle step is not ours: generation happens
 * on the egress side after we ask for it, and a single button would have to claim success
 * before any URL existed. So: ask, watch, then build — and the engineer can see how much of
 * the set is ready before committing to a manifest that is missing half the estate.
 *
 * The manifest is a bundle of bearer credentials — every URL in it grants read access to
 * vault data. Hence the warning, the short expiry, and revoke.
 */
export function RecoveryManifestDialog({
    open,
    onOpenChange,
    recoverySet,
    requestId,
}: {
    open: boolean
    onOpenChange: (open: boolean) => void
    /** Exactly one of these. requestId is a UUID string, not a number. */
    recoverySet?: number | null
    requestId?: string | null
}) {
    const { t } = useTranslation()
    const [status, setStatus] = useState<Status | null>(null)
    const [built, setBuilt] = useState<Built | null>(null)
    const [busy, setBusy] = useState<'request' | 'build' | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [copied, setCopied] = useState(false)
    const [polling, setPolling] = useState(false)
    /** Set when polling gave up with objects still pending, which unlocks building anyway. */
    const [pollTimedOut, setPollTimedOut] = useState(false)

    const scope = recoverySet != null ? { recoverySet } : { requestId }
    const scopeQuery = recoverySet != null
        ? `recoverySet=${encodeURIComponent(String(recoverySet))}`
        : `requestId=${encodeURIComponent(String(requestId ?? ''))}`

    const loadStatus = useCallback(async () => {
        const res = await fetch(`/api/recovery-manifest/status?${scopeQuery}`, { headers: authHeaders() })
        if (!res.ok) throw new Error(`Could not read status (${res.status})`)
        const body = await res.json()
        const next: Status = body?.data ?? body
        setStatus(next)
        return next
    }, [scopeQuery])

    const refresh = useCallback(async () => {
        try { await loadStatus() } catch (e: any) { setError(e?.message || 'Could not read status') }
    }, [loadStatus])

    useEffect(() => {
        if (!open) {
            setStatus(null); setBuilt(null); setError(null); setCopied(false)
            setPolling(false); setPollTimedOut(false)
            return
        }
        void refresh()
        // A manifest built earlier is still the manifest. Without this the screen looks as
        // though nothing was ever issued and the obvious move is to build a second one
        // describing exactly the same files.
        fetch(`/api/recovery-manifest/latest?${scopeQuery}`, { headers: authHeaders() })
            .then((r) => r.json())
            .then((body) => {
                const prev = body?.data ?? body
                if (prev && prev.url && !prev.expired) setBuilt(prev)
            })
            .catch(() => { /* no previous manifest is the normal case */ })
    }, [open, refresh, scopeQuery])

    /**
     * Watch generation finish.
     *
     * Generation happens on the egress side after we ask, so there is nothing to await — the
     * only way to know is to look. Polling stops the moment nothing is pending, and gives up
     * after ten minutes so a set with an object that will never generate does not leave a
     * request in flight forever. Giving up unlocks building anyway rather than dead-ending
     * someone mid-incident: an incomplete manifest they chose is better than no manifest.
     */
    useEffect(() => {
        if (!open || !polling) return
        let cancelled = false
        let ticks = 0
        const MAX_TICKS = 120          // 120 x 5s = 10 minutes
        const id = setInterval(async () => {
            if (cancelled) return
            ticks += 1
            try {
                const next = await loadStatus()
                if (next.pending === 0) { setPolling(false); return }
            } catch {
                // A failed poll is not a failed generation. Keep looking until the cap.
            }
            if (ticks >= MAX_TICKS) { setPolling(false); setPollTimedOut(true) }
        }, 5000)
        return () => { cancelled = true; clearInterval(id) }
    }, [open, polling, loadStatus])

    const post = async (path: string, setBusyTo: 'request' | 'build') => {
        setBusy(setBusyTo)
        setError(null)
        try {
            const res = await fetch(`/api/recovery-manifest/${path}`, {
                method: 'POST',
                headers: authHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify(scope),
            })
            const body = await res.json().catch(() => ({}))
            if (!res.ok) throw new Error(body?.message || `Failed (${res.status})`)
            return body?.data ?? body
        } finally {
            setBusy(null)
        }
    }

    const handleRequest = async () => {
        try {
            await post('request', 'request')
            setPollTimedOut(false)
            const next = await loadStatus()
            // Nothing to wait for if it is all already there.
            if (next.pending > 0) setPolling(true)
        } catch (e: any) {
            setError(e?.message || 'Could not request the URLs')
        }
    }

    const handleBuild = async () => {
        try { setBuilt(await post('build', 'build')) }
        catch (e: any) { setError(e?.message || 'Could not build the manifest') }
    }

    const copy = async () => {
        if (!built) return
        await navigator.clipboard.writeText(built.url)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }

    const scopeLabel = recoverySet != null
        ? t('recovery.manifest.scopeSet', 'Recovery set {{n}}', { n: recoverySet })
        : t('recovery.manifest.scopeRequest', 'Request {{id}}', { id: requestId })

    return (
        <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Link2 className="h-5 w-5" />
                        {t('recovery.manifest.title', 'Recovery manifest')}
                    </DialogTitle>
                    <DialogDescription>
                        {t('recovery.manifest.help', 'One link listing every pre-signed URL for {{scope}}, for the engineer to paste into the PXE recovery server console. The server pulls the files itself — it is never commanded.', { scope: scopeLabel })}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    {/* Step 1 */}
                    <div className="rounded-md border p-3 space-y-2">
                        <div className="flex items-center justify-between gap-4">
                            <div>
                                <div className="text-sm font-medium">{t('recovery.manifest.step1', '1. Ask for the URLs')}</div>
                                <p className="text-xs text-muted-foreground">
                                    {t('recovery.manifest.step1Help', 'Requests a pre-signed URL for every object in scope. Generation runs outside this screen, so this returns immediately and the URLs appear over the next few minutes.')}
                                </p>
                            </div>
                            <Button size="sm" variant="outline" onClick={handleRequest} disabled={!!busy}>
                                {busy === 'request'
                                    ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t('common.working', 'Working...')}</>
                                    : t('recovery.manifest.request', 'Request')}
                            </Button>
                        </div>
                    </div>

                    {/* Step 2 */}
                    <div className="rounded-md border p-3 space-y-2">
                        <div className="flex items-center justify-between gap-4">
                            <div className="text-sm font-medium">{t('recovery.manifest.step2', '2. How much is ready')}</div>
                            {polling ? (
                                <span className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    {t('recovery.manifest.watching', 'Watching for the URLs...')}
                                </span>
                            ) : (
                                <Button size="sm" variant="ghost" onClick={() => void refresh()} disabled={!!busy}>
                                    <RefreshCw className="mr-2 h-3.5 w-3.5" />
                                    {t('common.refresh', 'Refresh')}
                                </Button>
                            )}
                        </div>
                        {status ? (
                            <div className="grid grid-cols-4 gap-2 text-center">
                                {[
                                    { k: 'objects', label: t('recovery.manifest.objects', 'Objects'), v: status.objects },
                                    { k: 'ready', label: t('recovery.manifest.ready', 'Ready'), v: status.ready },
                                    { k: 'pending', label: t('recovery.manifest.pending', 'Pending'), v: status.pending },
                                    { k: 'files', label: t('recovery.manifest.files', 'Files'), v: status.files },
                                ].map((s) => (
                                    <div key={s.k} className="rounded-md bg-muted/50 p-2">
                                        <div className="text-lg font-medium">{s.v}</div>
                                        <div className="text-xs text-muted-foreground">{s.label}</div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        )}
                        {status && status.pending > 0 && (
                            <p className="text-xs text-amber-700 dark:text-amber-400">
                                {polling
                                    ? t('recovery.manifest.stillGenerating', '{{n}} object(s) still generating. Build unlocks when they are all ready.', { n: status.pending })
                                    : t('recovery.manifest.stillPending', '{{n}} object(s) have no URL yet. A manifest built now would not include them.', { n: status.pending })}
                            </p>
                        )}
                        {status && status.pending === 0 && status.files > 0 && (
                            <p className="flex items-center gap-1.5 text-xs text-emerald-700 dark:text-emerald-400">
                                <Check className="h-3.5 w-3.5" />
                                {t('recovery.manifest.allReady', 'Every object has its URLs.')}
                            </p>
                        )}
                    </div>

                    {/* Step 3 */}
                    <div className="rounded-md border p-3 space-y-2">
                        <div className="flex items-center justify-between gap-4">
                            <div>
                                <div className="text-sm font-medium">{t('recovery.manifest.step3', '3. Build the link')}</div>
                                <p className="text-xs text-muted-foreground">
                                    {t('recovery.manifest.step3Help', 'Snapshots the URLs that exist right now. The link stops working when the first URL inside it does.')}
                                </p>
                            </div>
                            <Button
                                size="sm"
                                onClick={handleBuild}
                                disabled={!!busy || polling || !status || status.files === 0 || (status.pending > 0 && !pollTimedOut)}
                            >
                                {busy === 'build'
                                    ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t('common.working', 'Working...')}</>
                                    : status && status.pending > 0 && pollTimedOut
                                        ? t('recovery.manifest.buildAnyway', 'Build with {{n}} of {{total}}', { n: status.ready, total: status.objects })
                                        : t('recovery.manifest.build', 'Build')}
                            </Button>
                        </div>

                        {pollTimedOut && status && status.pending > 0 && (
                            <p className="text-xs text-amber-700 dark:text-amber-400">
                                {t('recovery.manifest.gaveUp', 'Gave up waiting after ten minutes — {{n}} object(s) still have no URL. They may have no vault content. You can build without them, but the manifest will not cover them.', { n: status.pending })}
                            </p>
                        )}

                        {built && (
                            <div className="space-y-2 pt-1">
                                <div className="flex items-center gap-2">
                                    <code className="flex-1 overflow-x-auto whitespace-nowrap rounded bg-muted px-2 py-1.5 font-mono text-xs">
                                        {built.url}
                                    </code>
                                    <Button size="sm" variant="outline" onClick={copy}>
                                        {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                                    </Button>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    {t('recovery.manifest.built', '{{o}} object(s), {{f}} file(s). This link stops working at {{when}}.', {
                                        o: built.objectCount, f: built.fileCount,
                                        when: formatDateTime(built.expiresAt),
                                    })}
                                    {built.created && (
                                        <> {t('recovery.manifest.builtEarlier', 'Built {{when}}{{by}}.', {
                                            when: formatDateTime(built.created),
                                            by: built.createdBy ? ` by ${built.createdBy}` : '',
                                        })}</>
                                    )}
                                </p>
                                <p className="flex items-start gap-2 rounded-md border border-amber-300/60 bg-amber-50 p-2 text-xs text-amber-800 dark:bg-amber-950/20 dark:text-amber-300">
                                    <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                    <span>
                                        {t('recovery.manifest.credentialWarning', 'Anyone holding this link can read every file in the set — the URLs inside it are the credential. Give it to the engineer who needs it and nobody else. It can be revoked.')}
                                    </span>
                                </p>
                            </div>
                        )}
                    </div>

                    {error && (
                        <div className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/30 dark:text-red-300">
                            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={!!busy}>
                        {t('common.close', 'Close')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
